import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { LoadingBlock } from '../../components/ui.tsx';
import ProfileSection from '../../components/profile-section.tsx';
import { PasswordSection } from '../settings.tsx';
import { SettingsLeaf } from './settings-leaf.tsx';

export type AccountChild = 'account' | 'security' | 'connectedAccounts' | 'integrations';

const ACCOUNT_PAGES: Array<{ id: AccountChild; to: string }> = [
  { id: 'account', to: '/settings/account' },
  { id: 'security', to: '/settings/security' },
  { id: 'connectedAccounts', to: '/settings/connected-accounts' },
  { id: 'integrations', to: '/settings/integrations' },
];

/**
 * The signed-in person's own settings, split the way the mobile app's focused
 * Account screen is: who they are (profile and password), how they prove it
 * (Security: second codes, passkeys, sessions), which providers they sign in
 * with (Connected accounts), and whose mailbox and calendar the app may act
 * through (Integrations). Each is a page that mounts only its own card, so
 * the MFA-setup redirect lands on Security without waiting on mailbox OAuth
 * status, and the profile page renders neither form.
 *
 * These are the person's own, so — as on /settings — nothing gates them by
 * role or plan; crew keep their own /crew-account page.
 */
export function AccountSettingsFrame({
  current,
  anchorId,
  children,
}: {
  current: AccountChild;
  /** The id this card's wrapper had on /settings, when it had one of its own. */
  anchorId?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <SettingsLeaf
      title={t(`settings.accountHub.titles.${current}`)}
      tabsLabel={t('settings.accountHub.tabsLabel')}
      tabs={ACCOUNT_PAGES.map((page) => ({
        to: page.to,
        label: t(`settings.accountHub.tabs.${page.id}`),
        current: page.id === current,
      }))}
      anchorId={anchorId}
    >
      {children}
    </SettingsLeaf>
  );
}

/** /settings/account: profile and password. */
export default function AccountPage() {
  const { t } = useTranslation();
  const { company, user, refresh } = useSession();
  const { hash } = useLocation();
  const changePassword = useMutation({
    mutationFn: (payload: { currentPassword?: string; newPassword: string }) => authApi.changePassword(payload),
    // An account that had no password now has one, and the connected-accounts
    // page keys its "you cannot remove your last way in" rule off that.
    onSuccess: () => void refresh(),
  });

  // "Set a password" on the connected-accounts page links here with the
  // form's heading as the fragment. The router does not scroll to one.
  const ready = company !== null;
  useEffect(() => {
    if (ready && hash === '#password-heading') document.getElementById('password-heading')?.scrollIntoView();
  }, [ready, hash]);

  if (!company) return <LoadingBlock label={t('settings.loadingSettings')} />;

  return (
    <AccountSettingsFrame current="account" anchorId="settings-account">
      <ProfileSection />
      <PasswordSection
        hasPassword={user?.hasPassword ?? true}
        onSubmit={(payload) => changePassword.mutateAsync(payload)}
        busy={changePassword.isPending}
      />
    </AccountSettingsFrame>
  );
}
